import { Button, Stack, Tooltip, Typography } from '@mui/material'
import { DataGrid, GridColDef, GridOverlay } from '@mui/x-data-grid'
import React, { useCallback, useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'

import { TruncatedTextWithTooltip } from 'components/common/TruncatedTextWithTooltip'
import Icon from 'components/Icon'
import { TestSuiteScenarioInfo } from 'components/test-suites/forms/test-suite-composition/TestSuiteScenarioInfo'
import { TestSuiteTestDetailsDrawer } from 'components/test-suites/forms/test-suite-composition/TestSuiteTestDetailsDrawer'
import {
  getOwningTeamLabel,
  getTestSuiteScenarioTargetingInfo,
  POLICY_CATEGORY_OPTIONS,
  TestSuiteFormValues,
  TestSuiteScenario,
  TestSuiteTestRow,
  USER_MESSAGES
} from 'components/test-suites/forms/test-suite-form-utils'
import { useTestSuiteFormContext } from 'components/test-suites/forms/TestSuiteFormContext'
import { Team } from 'types/api/teams'
import {
  defaultColumnProps,
  defaultGridProps,
  getGridStyles
} from 'utils/mui-components/data-grid-utils'
import { createLookupMapFromArray } from 'utils/obj'
import { capitalize } from 'utils/strings'

const TestSuiteScenarioTableEmptyState: React.FC = () => {
  return (
    <GridOverlay>
      <Typography>No Scenarios Selected</Typography>
    </GridOverlay>
  )
}

export const TestSuiteCompositionTable: React.FC<{
  canIncludeActions: boolean
  teams: Team[]
}> = ({ canIncludeActions, teams }) => {
  const { scenariosLookup } = useTestSuiteFormContext()

  const { control, setValue } = useFormContext<TestSuiteFormValues>()
  const scenariosValue = useWatch({
    control,
    name: 'scenarios'
  })

  const teamIdMap = useMemo(
    () => createLookupMapFromArray(teams, 'identifier'),
    [teams]
  )

  /**
   * This function must be able to detect and update either a scenario-to-promote or an existing policy.
   */
  const handleUpdateTests = useCallback(
    (newTest: TestSuiteScenario) => {
      setValue(
        'scenarios',
        scenariosValue.map((scenarioValue) => {
          if (scenarioValue.scenarioId === newTest.scenarioId) {
            return newTest
          }

          return scenarioValue
        }),
        {
          shouldValidate: true,
          shouldDirty: true
        }
      )
    },
    [scenariosValue, setValue]
  )

  const handleDeleteTest = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    testSuiteRowId: string
  ) => {
    e.preventDefault()
    const scenariosToKeep = scenariosValue.filter(
      (existingScenario: TestSuiteScenario) =>
        existingScenario.scenarioId !== testSuiteRowId
    )

    setValue('scenarios', scenariosToKeep, {
      shouldValidate: true,
      shouldDirty: true
    })
  }

  const testRows: TestSuiteTestRow[] = useMemo(() => {
    return scenariosValue.map((testSuiteScenario) => {
      const scenario = scenariosLookup[testSuiteScenario.scenarioId]
      const teamId =
        scenario && 'org_id' in scenario ? scenario.org_id : undefined

      const owningTeam = teamId ? teamIdMap[teamId] : undefined

      return {
        ...testSuiteScenario,
        owningTeamLabel:
          teamId && owningTeam ? getOwningTeamLabel(teamId, owningTeam) : null,
        scenario
      }
    })
  }, [scenariosLookup, scenariosValue, teamIdMap])

  // Sort the selected scenarios and existing policies by category so that they are grouped together in the table
  const sortedTestRows = useMemo(() => {
    const policyCategoryOptionsValues = POLICY_CATEGORY_OPTIONS.map(
      (option) => option.value
    )
    return testRows.sort(
      (test1, test2) =>
        policyCategoryOptionsValues.indexOf(test1.policyCategory) -
        policyCategoryOptionsValues.indexOf(test2.policyCategory)
    )
  }, [testRows])

  const tableColumns: GridColDef<TestSuiteTestRow>[] = [
    {
      ...defaultColumnProps,
      field: 'scenarioName',
      headerName: 'Scenario',
      flex: 2,
      renderCell: (params) => {
        // Renders the name of the source scenario, defaults to the name of the test if no source scenario
        const name = params.row.scenario?.name ?? params.row.name
        return (
          <TruncatedTextWithTooltip
            data-testid="test-suite-composition-table-cell-scenario-name"
            tooltipProps={{
              title: (
                <TestSuiteScenarioInfo
                  id={params.row.scenarioId}
                  name={name}
                  owningTeamLabel={params.row.owningTeamLabel ?? ''}
                />
              )
            }}
          >
            {name}
          </TruncatedTextWithTooltip>
        )
      }
    },
    {
      ...defaultColumnProps,
      field: 'scenarioType',
      headerName: 'Scenario Type',
      flex: 1,
      renderCell: ({ row }) => {
        if (!row.scenarioType) {
          return (
            <Tooltip title={USER_MESSAGES.scenarioInfoUnknown}>
              <Typography>n/a</Typography>
            </Tooltip>
          )
        }

        const value = capitalize(row.scenarioType)

        return (
          <TruncatedTextWithTooltip tooltipProps={{ title: value }}>
            <Typography noWrap>{value}</Typography>
          </TruncatedTextWithTooltip>
        )
      }
    },
    {
      ...defaultColumnProps,
      field: 'testName',
      headerName: 'Test Name',
      flex: 2,
      renderCell: (params) => {
        // Renders the name of the scenario-to-promote or the existing Policy
        const name = params.row.name
        return (
          <TruncatedTextWithTooltip
            data-testid="test-suite-composition-table-cell-test-name"
            tooltipProps={{
              title: <Typography variant="body2">{name}</Typography>
            }}
          >
            {name}
          </TruncatedTextWithTooltip>
        )
      }
    },
    {
      ...defaultColumnProps,
      field: 'targets',
      headerName: 'Targets',
      flex: 1,
      renderCell: ({ row }) => {
        if (!row.scenario) {
          return null
        }

        const targetInfo = getTestSuiteScenarioTargetingInfo(row.scenario)

        if (!targetInfo.hasInfraNode && !targetInfo.hasFailureFlagNode) {
          return null
        }

        const value = targetInfo.hasFailureFlagNode
          ? 'Failure Flags'
          : 'Infrastructure'

        return (
          <TruncatedTextWithTooltip tooltipProps={{ title: value }}>
            <Typography noWrap>{value}</Typography>
          </TruncatedTextWithTooltip>
        )
      }
    },
    {
      ...defaultColumnProps,
      field: 'policyCategory',
      headerName: 'Category',
      flex: 0.8,
      valueGetter: ({ row }) => row.policyCategory,
      renderCell: ({ row }) => {
        const policyCategoryOptionLabel = POLICY_CATEGORY_OPTIONS.find(
          (option) => option.value === row.policyCategory
        )?.label

        return (
          <TruncatedTextWithTooltip
            tooltipProps={{ title: policyCategoryOptionLabel }}
          >
            <Typography noWrap>{policyCategoryOptionLabel}</Typography>
          </TruncatedTextWithTooltip>
        )
      }
    }
  ]

  const actionsColumn: GridColDef<TestSuiteTestRow> = {
    ...defaultColumnProps,
    align: 'right',
    field: '_actions',
    headerAlign: 'right',
    headerName: 'Actions',
    flex: 2,
    renderCell: (params) => {
      const testToEdit = scenariosValue.find(
        (existingScenario: TestSuiteScenario) =>
          existingScenario.scenarioId === params.row.scenarioId
      )

      return (
        <Stack direction="row" spacing={1}>
          <TestSuiteTestDetailsDrawer
            buttonProps={{
              size: 'small',
              startIcon: <Icon icon="edit" />
            }}
            buttonText="Edit"
            handleSubmit={handleUpdateTests}
            scenario={undefined}
            testToEdit={testToEdit}
          />
          <Button
            color="error"
            size="small"
            startIcon={<Icon icon="xmark" />}
            variant="contained"
            onClick={(e) => handleDeleteTest(e, params.row.scenarioId)}
          >
            Remove
          </Button>
        </Stack>
      )
    }
  }

  if (canIncludeActions) {
    tableColumns.push(actionsColumn)
  }

  return (
    <DataGrid<TestSuiteTestRow>
      {...defaultGridProps}
      columns={tableColumns}
      components={{
        NoRowsOverlay: TestSuiteScenarioTableEmptyState
      }}
      getRowId={(row) => row.reliabilityTestId ?? row.scenarioId}
      rows={sortedTestRows}
      sx={getGridStyles()}
    />
  )
}
